// src/components/Chat/components/ChatUI/UsersList.jsx
import React, { useMemo } from "react";
import { FiSearch, FiUser, FiX } from "react-icons/fi";

const UsersList = ({
  currentUser,
  employees,
  onlineIds,
  conversations,
  search,
  setSearch,
  loadingEmployees,
  socketConnected,
  onSelectUser,
  activeUserId,
  onClose,
}) => {
  const onlineSet = useMemo(
    () => new Set(onlineIds || []), // ✅ Keep as numbers
    [onlineIds]
  );

  const usersWithStatus = useMemo(() => {
    return employees
      .filter((e) => e.id !== currentUser?.id)
      .map((emp) => {
        const conv = conversations[emp.id] || {};
        const lastMessage = conv.messages?.[conv.messages.length - 1];

        return {
          ...emp,
          online: onlineSet.has(emp.id), // ✅ Direct number comparison
          unread: conv.unread || 0,
          lastMessage: lastMessage || null,
          typing: conv.typing || false,
        };
      })
      .sort((a, b) => {
        if (a.online && !b.online) return -1;
        if (!a.online && b.online) return 1;
        if (a.unread > 0 && b.unread === 0) return -1;
        if (a.unread === 0 && b.unread > 0) return 1;
        const ta = a.lastMessage
          ? new Date(a.lastMessage.createdAt).getTime()
          : 0;
        const tb = b.lastMessage
          ? new Date(b.lastMessage.createdAt).getTime()
          : 0;
        return tb - ta;
      });
  }, [employees, conversations, onlineSet, currentUser]);

  const filteredUsers = useMemo(() => {
    const term = search.toLowerCase();
    if (!term) return usersWithStatus;

    return usersWithStatus.filter((u) => {
      return (
        u.name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.designation?.toLowerCase().includes(term)
      );
    });
  }, [usersWithStatus, search]);

  const getAvatarUrl = (emp) => {
    if (emp?.photo) return emp.photo.trim();
    return `https://ui-avatars.com/api/?background=0D8ABC&color=fff&name=${encodeURIComponent(
      emp?.name || "User"
    )}`;
  };

  const formatMessageTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <>
      {/* Header */}
      <div className="px-2 xs:px-3 py-2 xs:py-2.5 bg-gradient-to-r from-blue-900 to-blue-950 text-white flex items-center justify-between shadow-md border-b-2 border-yellow-400">
        <div className="flex items-center gap-2 xs:gap-3 min-w-0">
          <div className="relative flex-shrink-0">
            <img
              src={getAvatarUrl(currentUser)}
              alt={currentUser.name}
              className="w-8 xs:w-9 h-8 xs:h-9 rounded-full border-2 border-yellow-400 object-cover"
            />
            <span
              className={`absolute -bottom-0.5 -right-0.5 w-2.5 xs:w-3 h-2.5 xs:h-3 rounded-full border-2 border-white ${
                socketConnected ? "bg-green-500 shadow-lg" : "bg-gray-400"
              }`}
            />
          </div>
          <div className="min-w-0">
            <h2 className="text-xs xs:text-sm font-bold text-yellow-300 truncate">
              {currentUser.name}
            </h2>
            <p className="text-xs text-blue-100 truncate">
              {currentUser.designation || "Employee"}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 xs:p-2 hover:bg-blue-800 rounded-full transition-all flex-shrink-0"
        >
          <FiX className="text-sm xs:text-base" />
        </button>
      </div>

      {/* Search */}
      <div className="px-2 xs:px-3 py-1.5 xs:py-2 bg-blue-100 border-b-2 border-yellow-300">
        <div className="relative">
          <FiSearch className="absolute left-2 xs:left-2.5 top-1/2 -translate-y-1/2 text-blue-900 font-bold text-xs xs:text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="w-full bg-white pl-6 xs:pl-8 pr-2 py-1 xs:py-1.5 rounded-lg border-2 border-blue-900 focus:outline-none focus:ring-0 focus:border-yellow-400 focus:shadow-lg text-xs font-semibold text-gray-900 placeholder-blue-600"
          />
        </div>
      </div>

      {/* Users List */}
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        {loadingEmployees ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin text-blue-900 text-2xl font-bold">
              ⟳
            </div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-blue-600 p-3 xs:p-4">
            <FiUser className="text-xl xs:text-2xl mb-1 xs:mb-2" />
            <p className="text-xs xs:text-sm font-semibold text-center">
              No contacts found
            </p>
          </div>
        ) : (
          filteredUsers.map((u) => {
            const isActive = activeUserId === u.id;
            const lastMsg = u.lastMessage;

            return (
              <button
                key={u.id}
                onClick={() => onSelectUser(u.id)}
                className={`w-full px-2 xs:px-2.5 py-1.5 xs:py-2 flex items-center gap-2 text-left border-b transition-all hover:bg-blue-50 ${
                  isActive
                    ? "bg-gradient-to-r from-yellow-300 to-yellow-200 border-l-3 border-l-yellow-600 border-b-blue-200"
                    : "border-b-blue-100 bg-blue-50"
                }`}
              >
                <div className="relative flex-shrink-0">
                  <img
                    src={getAvatarUrl(u)}
                    alt={u.name}
                    className="w-8 xs:w-9 h-8 xs:h-9 rounded-full border-2 border-blue-200 object-cover"
                  />
                  <span
                    className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-1.5 border-white shadow-md ${
                      u.online ? "bg-green-500" : "bg-gray-400"
                    }`}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start gap-1">
                    <h3
                      className={`text-xs font-bold truncate ${
                        isActive ? "text-blue-900" : "text-gray-900"
                      }`}
                    >
                      {u.name}
                    </h3>
                    {lastMsg && (
                      <span
                        className={`text-[10px] xs:text-xs whitespace-nowrap flex-shrink-0 ${
                          isActive
                            ? "text-blue-700 font-semibold"
                            : "text-gray-400"
                        }`}
                      >
                        {formatMessageTime(
                          lastMsg.createdAt || lastMsg.timestamp
                        )}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between gap-1 mt-0.5">
                    <p
                      className={`text-[10px] xs:text-xs truncate max-w-[100px] leading-tight ${
                        isActive
                          ? "text-blue-800 font-semibold"
                          : "text-gray-500"
                      }`}
                    >
                      {lastMsg
                        ? lastMsg.type === "text"
                          ? lastMsg.message || "📎 Attachment"
                          : `📎 ${lastMsg.type}`
                        : u.online
                        ? "🟢 Online"
                        : "Offline"}
                    </p>

                    {u.unread > 0 && (
                      <span className="bg-yellow-400 text-blue-900 text-xs font-black rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 shadow-md">
                        {u.unread > 9 ? "9+" : u.unread}
                      </span>
                    )}
                  </div>

                  {u.typing && (
                    <p className="text-[10px] xs:text-xs text-blue-900 italic font-semibold mt-0.5 leading-tight">
                      ✏️ typing...
                    </p>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </>
  );
};

export default UsersList;
